import addonOrderQueries from "$/lib/queries/addon-order-queries.ts"
import bikeConditionQueries from "$/lib/queries/bike-condition-queries.ts"
import receiptQueries from "$/lib/queries/receipt-queries.ts"
import {
  BatchUpdateAllTasksIsDone,
  EvaluatedPartTask,
  Route,
  ServicePackageTask,
  ServiceWashTask,
  UpdateEvaluatedPartTask,
  UpdateManualServiceActionTask,
  UpdatePackageTask,
  UpdateWashTask,
  WorkOrder,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { request } from "../api"
import workTimeRecordingQueries from "./work-time-recording-queries"

async function fetchWorkOrder(jobId: string) {
  return await request<WorkOrder>({
    url: populateRoute(Route.WORK_ORDER__FIND_ONE, jobId),
    method: "GET",
  })
}

async function deleteWashTask(jobId: string, washSnapshotId: string) {
  return await request({
    url: populateRoute(
      Route.WORK_ORDER__DELETE_WASH_TASK,
      washSnapshotId,
      jobId,
    ),
    method: "DELETE",
  })
}

async function deleteManualServiceActionTask(
  jobId: string,
  manualServiceActionId: string,
) {
  return await request({
    url: populateRoute(
      Route.WORK_ORDER__DELETE_MANUAL_SERVICE_ACTION_TASK,
      manualServiceActionId,
      jobId,
    ),
    method: "DELETE",
  })
}

async function deletePartTask(jobId: string, partId: string) {
  return await request({
    url: populateRoute(Route.WORK_ORDER__DELETE_PART_TASK, partId, jobId),
    method: "DELETE",
  })
}

async function deletePackageTask(jobId: string, packageTaskId: string) {
  return await request({
    url: populateRoute(
      Route.WORK_ORDER__DELETE_PACKAGE_TASK,
      packageTaskId,
      jobId,
    ),
    method: "DELETE",
  })
}

async function updatePackageTask(
  jobId: string,
  packageTaskId: string,
  task: UpdatePackageTask,
): Promise<ServicePackageTask> {
  return request({
    url: populateRoute(
      Route.WORK_ORDER__UPDATE_PACKAGE_TASK,
      packageTaskId,
      jobId,
    ),
    method: "PATCH",
    data: task,
  })
}

async function updateWashTask(
  jobId: string,
  washSnapshotId: string,
  task: UpdateWashTask,
): Promise<ServiceWashTask> {
  return request({
    url: populateRoute(
      Route.WORK_ORDER__UPDATE_WASH_TASK,
      washSnapshotId,
      jobId,
    ),
    method: "PATCH",
    data: task,
  })
}

async function updatePartTask(
  jobId: string,
  partId: string,
  task: UpdateEvaluatedPartTask,
): Promise<EvaluatedPartTask> {
  return request({
    url: populateRoute(Route.WORK_ORDER__UPDATE_PART_TASK, partId, jobId),
    method: "PATCH",
    data: task,
  })
}

async function updateManualServiceActionTask(
  jobId: string,
  manualServiceActionId: string,
  task: UpdateManualServiceActionTask,
): Promise<UpdateManualServiceActionTask> {
  return request({
    url: populateRoute(
      Route.WORK_ORDER__UPDATE_MANUAL_SERVICE_ACTION_TASK,
      manualServiceActionId,
      jobId,
    ),
    method: "PATCH",
    data: task,
  })
}

async function fetchAllTasksIsDone(jobId: string) {
  return await request<boolean>({
    url: populateRoute(Route.WORK_ORDER__GET_ALL_IS_DONE, jobId),
    method: "GET",
  })
}

async function batchUpdateAllTasksIsDone(
  jobId: string,
  updates: BatchUpdateAllTasksIsDone,
): Promise<void> {
  return request({
    url: populateRoute(Route.WORK_ORDER__BATCH_UPDATE_ALL_TASKS_IS_DONE, jobId),
    method: "PATCH",
    data: updates,
  })
}

export const allTasksDoneQueryOptions = (jobId: string) =>
  queryOptions({
    queryFn: () => fetchAllTasksIsDone(jobId),
    queryKey: ["isAllTasksDone", jobId],
  })

export function useWorkOrderMutations(jobId: string) {
  const queryClient = useQueryClient()

  const onSuccess = async () => {
    await Promise.all([
      queryClient.invalidateQueries(workOrderQueries.detail(jobId)),
      queryClient.invalidateQueries(allTasksDoneQueryOptions(jobId)),
      queryClient.invalidateQueries(receiptQueries.detail(jobId)),
      queryClient.invalidateQueries(bikeConditionQueries.detail(jobId)),
      queryClient.invalidateQueries(addonOrderQueries.detail(jobId)),
      // Ticking a row done stops its stopwatch on the API
      queryClient.invalidateQueries(workTimeRecordingQueries.byJob(jobId)),
    ])
  }

  const updatePartTaskMutation = useMutation({
    mutationFn: ({
      partId,
      data,
    }: { partId: string; data: UpdateEvaluatedPartTask }) =>
      updatePartTask(jobId, partId, data),
    onSuccess,
  })

  const updateManualServiceActionTaskMutation = useMutation({
    mutationFn: ({
      manualServiceActionId,
      data,
    }: {
      manualServiceActionId: string
      data: UpdateManualServiceActionTask
    }) => updateManualServiceActionTask(jobId, manualServiceActionId, data),
    onSuccess,
  })

  const updatePackageTaskMutation = useMutation({
    mutationFn: ({
      packageTaskId,
      data,
    }: { packageTaskId: string; data: UpdatePackageTask }) =>
      updatePackageTask(jobId, packageTaskId, data),
    onSuccess,
  })

  const updateWashTaskMutation = useMutation({
    mutationFn: ({
      washSnapshotId,
      data,
    }: { washSnapshotId: string; data: UpdateWashTask }) =>
      updateWashTask(jobId, washSnapshotId, data),
    onSuccess,
  })

  const deleteManualServiceActionTaskMutation = useMutation({
    mutationFn: (manualServiceActionId: string) =>
      deleteManualServiceActionTask(jobId, manualServiceActionId),
    onSuccess,
  })

  const deletePackageTaskMutation = useMutation({
    mutationFn: (packageTaskId: string) =>
      deletePackageTask(jobId, packageTaskId),
    onSuccess,
  })

  const deletePartTaskMutation = useMutation({
    mutationFn: (partId: string) => deletePartTask(jobId, partId),
    onSuccess,
  })

  const deleteWashTaskMutation = useMutation({
    mutationFn: (washSnapshotId: string) =>
      deleteWashTask(jobId, washSnapshotId),
    onSuccess,
  })

  const batchUpdateAllTasksIsDoneMutation = useMutation({
    mutationFn: (updates: BatchUpdateAllTasksIsDone) =>
      batchUpdateAllTasksIsDone(jobId, updates),
    onSuccess,
  })

  return {
    updateWashTaskMutation,
    deleteWashTaskMutation,
    updateManualServiceActionTaskMutation,
    deleteManualServiceActionTaskMutation,
    updatePartTaskMutation,
    deletePartTaskMutation,
    updatePackageTaskMutation,
    deletePackageTaskMutation,
    batchUpdateAllTasksIsDoneMutation,
  }
}

const workOrderQueries = {
  all: () => ["service-todo"],
  details: () => [...workOrderQueries.all(), "detail"],
  detail: (id: string) =>
    queryOptions({
      queryKey: [...workOrderQueries.details(), id],
      queryFn: () => fetchWorkOrder(id),
    }),
}

export default workOrderQueries
